import { browserHistory } from 'react-router';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { stringify } from 'query-string';

import { pipe } from './pipe';
import { omitEmpty } from '../blocks/form';

const slashClutters = /\/+/g;
const slashTail = /\/+$/;
const slashHead = /^\/*/;

const append = (suffux) => (path) => `${path}${suffux}`;

const withQuery = (path, query) => {
  if (isEmpty(query)) return path;
  const queryString = stringify(query);
  return `${path}?${queryString}`;
};

export const prefix = get(window, 'beagly.path', '')
  .replace(slashClutters, '/')
  .replace(slashTail, '');

export const getHomeRoute = () => prefix.replace(slashHead, '/');

export const getHomePath = () => `${prefix}/`;

export const getSharePath = (organizationId, surveyId) =>
  `${prefix}/${organizationId}/surveys/${surveyId}/share`;
export const getSurveyLinkPath = pipe(getSharePath, append('/survey-link'));
export const getSurveyEmbedPath = pipe(getSharePath, append('/survey-embed'));

export const getEmailCampaignsPath = pipe(getSharePath, append('/email-campaigns'));

export const getTextCampaignsPath = pipe(getSharePath, append('/text-campaigns'));
export const getTextCampaignsAddPath = (organizationId, surveyId) =>
  `${getTextCampaignsPath(organizationId, surveyId)}/add`;
export const getTextCampaignPath = (organizationId, surveyId, campaignId, view) =>
  view
    ? `${getTextCampaignsPath(organizationId, surveyId)}/text/${campaignId}/${view}`
    : `${getTextCampaignsPath(organizationId, surveyId)}/text/${campaignId}`;

export const getTextCampaignAddParticipantPath = (organizationId, surveyId, campaignId) =>
  getTextCampaignPath(organizationId, surveyId, campaignId, 'participants/add');

export const getTextCampaignParticipantPath = (
  organizationId,
  surveyId,
  campaignId,
  participantId,
) => getTextCampaignPath(organizationId, surveyId, campaignId, `participants/${participantId}`);

export const getTextCampaignAddMessagePath = (organizationId, surveyId, campaignId) =>
  getTextCampaignPath(organizationId, surveyId, campaignId, 'messages/add');

export const getTextCampaignMessagePath = (organizationId, surveyId, campaignId, messageId) =>
  getTextCampaignPath(organizationId, surveyId, campaignId, `messages/${messageId}`);

export const getTextCampaignUploadParticipantsPath = (organizationId, surveyId, campaignId) =>
  getTextCampaignPath(organizationId, surveyId, campaignId, 'participants/upload');

export const getTextCampaignUploadOptedOutPath = (organizationId, surveyId, campaignId) =>
  getTextCampaignPath(organizationId, surveyId, campaignId, 'participants/upload_optedout');

export const getEmailCampaignsAddPath = (organizationId, surveyId) =>
  `${getEmailCampaignsPath(organizationId, surveyId)}/add`;

export const getAnonymousCampaignsPath = pipe(getSharePath, append('/anonymous-campaigns'));

export const getAnonymousCampaignsAddPath = (organizationId, surveyId) =>
  `${getAnonymousCampaignsPath(organizationId, surveyId)}/add`;

export const getAnonymousCampaignPath = (organizationId, surveyId, campaignId, view) =>
  view
    ? `${getAnonymousCampaignsPath(organizationId, surveyId)}/anonymous/${campaignId}/${view}`
    : `${getAnonymousCampaignsPath(organizationId, surveyId)}/anonymous/${campaignId}`;

export const getEmailCampaignPath = (organizationId, surveyId, campaignType, campaignId, view) =>
  view
    ? `${getEmailCampaignsPath(organizationId, surveyId)}/${campaignType}/${campaignId}/${view}`
    : `${getEmailCampaignsPath(organizationId, surveyId)}/${campaignType}/${campaignId}`;
export const getEmailCampaignAddParticipantPath = (organizationId, surveyId, campaignId) =>
  getEmailCampaignPath(organizationId, surveyId, 'email', campaignId, 'participants/add');

export const getEmailCampaignParticipantPath = (
  organizationId,
  surveyId,
  campaignId,
  participantId,
) =>
  getEmailCampaignPath(
    organizationId,
    surveyId,
    'email',
    campaignId,
    `participants/${participantId}`,
  );

export const getEmailCampaignUploadParticipantsPath = (organizationId, surveyId, campaignId) =>
  getEmailCampaignPath(organizationId, surveyId, 'email', campaignId, 'participants/upload');

export const getCampaignMessagePath = (organizationId, surveyId, campaignId, messageId) =>
  getEmailCampaignPath(organizationId, surveyId, 'email', campaignId, `messages/${messageId}`);

export const getCampaignAddMessagePath = (organizationId, surveyId, campaignId) =>
  getEmailCampaignPath(organizationId, surveyId, 'email', campaignId, 'messages/add');

export const getGoogleDocsEportPath = pipe(getSharePath, append('/google-docs-export'));

export const getPrivacyPolicyPath = () => `${prefix}/legal/privacy-policy`;
export const getPrivacyShieldPolicyPath = () => `${prefix}/legal/privacy-shield-policy`;
export const getTermsOfUsePath = () => `${prefix}/legal/terms-of-use`;

export const getSurveyPreviewPath = (organizationId, surveyId, pageNumber) =>
  pageNumber
    ? `${prefix}/${organizationId}/surveys/${surveyId}/preview/${pageNumber}`
    : `${prefix}/${organizationId}/surveys/${surveyId}/preview`;

const getBaseSurveyPublicPath = (accessToken) =>
  `${window.location.origin}${getHomePath()}take/${accessToken}`;

export const getSurveyPublicPath = (accessToken, invitationToken) =>
  invitationToken
    ? `${getBaseSurveyPublicPath(accessToken)}/${invitationToken}`
    : `${getBaseSurveyPublicPath(accessToken)}`;

export const getSurveysPath = (organizationId, page) =>
  page ? `${prefix}/${organizationId}/surveys/page/${page}` : `${prefix}/${organizationId}/surveys`;

export const getSurveyReportsPath = (organizationId, surveyId) =>
  `${getSurveysPath(organizationId)}/${surveyId}/reports`;

export const getSurveyStatusReportPath = pipe(getSurveyReportsPath, append('/survey-status'));
export const getSurveySlideCompletionRatesReportPath = pipe(
  getSurveyReportsPath,
  append('/slide-completion-rates'),
);
export const getSurveyDurationReportPath = pipe(getSurveyReportsPath, append('/duration'));
export const getSurveyRewardsReportPath = pipe(getSurveyReportsPath, append('/rewards'));
export const getSurveySocialMediaAuthReportPath = pipe(
  getSurveyReportsPath,
  append('/social-media'),
);
export const getSurveyElementResultsReportPath = pipe(
  getSurveyReportsPath,
  append('/element-results'),
);

export const getSurveyRawDataRepotPath = (organizationId, surveyId, query) => {
  const path = `${getSurveyReportsPath(organizationId, surveyId)}/raw-data`;
  return withQuery(path, query);
};
export const getSurveyRawDataItemPath = (organizationId, surveyId, resultId, tabId, query) => {
  const path = `${getSurveyRawDataRepotPath(organizationId, surveyId)}/${resultId}/${tabId}`;
  return withQuery(path, query);
};

export const getSurveyDemographicDataRepotPath = pipe(
  getSurveyReportsPath,
  append('/demographic-data'),
);
export const getSurveyGeographicDataRepotPath = pipe(
  getSurveyReportsPath,
  append('/geographic-data'),
);
export const getSurveySourceDataRepotPath = pipe(getSurveyReportsPath, append('/utm-contents'));
export const getSurveyStartTimesRepotPath = pipe(getSurveyReportsPath, append('/start-times'));
export const getSurveyGiftbitRepotPath = (organizationId, surveyId, query) => {
  const surveyReportsPath = getSurveyReportsPath(organizationId, surveyId);
  const basePath = append('/giftbit-rewards')(surveyReportsPath);
  return withQuery(basePath, query);
};

export const getSurveyGiftbitRepotItemPath = (organizationId, surveyId, query, itemId) => {
  const basePath = getSurveyGiftbitRepotPath(organizationId, surveyId);
  const itemPath = `${basePath}/${itemId}`;
  return withQuery(itemPath, query);
};

export const getSurveyRewardsBasePath = (organizationId, surveyId) =>
  `${getSurveysPath(organizationId)}/${surveyId}/rewards`;
export const getSurveyRewardsPath = (organizationId, surveyId) =>
  getSurveyRewardsBasePath(organizationId, surveyId);
export const getSurveyRewardCodesPath = (organizationId, surveyId) =>
  `${getSurveyRewardsBasePath(organizationId, surveyId)}/codes`;
export const getSurveyRewardCodesImportPath = (organizationId, surveyId) =>
  `${getSurveyRewardCodesPath(organizationId, surveyId)}/import`;

export const getSurveySettingsPath = (organizationId, surveyId) =>
  `${getSurveysPath(organizationId)}/${surveyId}/settings`;
export const getSurveyGeneralSettingsPath = (organizationId, surveyId) =>
  `${getSurveySettingsPath(organizationId, surveyId)}/survey`;
export const getSurveySecuritySettingsPath = (organizationId, surveyId) =>
  `${getSurveySettingsPath(organizationId, surveyId)}/security`;
export const getSurveyNotificationsPath = (organizationId, surveyId) =>
  `${getSurveySettingsPath(organizationId, surveyId)}/notifications`;
export const getSurveyHistoryPath = (organizationId, surveyId) =>
  `${getSurveySettingsPath(organizationId, surveyId)}/history`;
export const getSurveyExportPath = (organizationId, surveyId) =>
  `${getSurveySettingsPath(organizationId, surveyId)}/integrations`;

// NOTE: DataSettings is not implemrented yet
/*
export const getSurveyDataSettingsPath = (organizationId, surveyId) =>
  `${getSurveySettingsPath(organizationId, surveyId)}/data`;
export const getSurveyDataVariableAddPath = (organizationId, surveyId) =>
  `${getSurveyDataSettingsPath(organizationId, surveyId)}/add`;
export const getSurveyDataVariablePath = (organizationId, surveyId, variable) =>
  `${getSurveyDataSettingsPath(organizationId, surveyId)}/${variable}`;
*/

export const getGiftbitPath = (organizationId, surveyId) =>
  `${getSurveyRewardsBasePath(organizationId, surveyId)}/giftbit`;
export const getGiftbitAddPath = (organizationId, surveyId) =>
  `${getGiftbitPath(organizationId, surveyId)}/add`;

export const getLoginPath = () => `${prefix}/auth`;

export const getParticipantsPath = (organizationId) => `${prefix}/${organizationId}/participants`;
export const getParticipantDetailPath = (organizationId, participantId) =>
  `${prefix}/${organizationId}/participants/${participantId}`;
export const getParticipantListPath = pipe(getParticipantsPath, append('/list'));
export const getParticipantGroupsPath = pipe(getParticipantsPath, append('/groups'));
export const getParticipantGroupsAddPath = pipe(getParticipantGroupsPath, append('/add'));
export const getParticipantGroupsItemPath = (organizationId) => (groupId) =>
  `${getParticipantGroupsPath(organizationId)}/${groupId}`;
export const getParticipantGroupsEditPath = (organizationId, groupId) =>
  `${getParticipantGroupsPath(organizationId)}/edit/${groupId}`;

export const getProfilePath = () => `${prefix}/profile`;
export const getProfileEditPath = () => `${getProfilePath()}/edit`;
export const getProfilePasswordPath = () => `${getProfilePath()}/password`;

export const getResetPasswordPath = () => `${prefix}/auth/reset-password`;
export const getSetPasswordPath = () => `${prefix}/auth/set-password`;
export const getMembershipsPath = (organizationId) => `${prefix}/${organizationId}/memberships`;
export const getMembershipsNewPath = (organizationId) =>
  `${prefix}/${organizationId}/memberships/new`;
export const getMembershipsItemPath = (organizationId, memberId) =>
  `${getMembershipsPath(organizationId)}/${memberId}`;
export const getMembershipsReActivatePath = (organizationId, memberId) =>
  `${getMembershipsPath(organizationId)}/recover/${memberId}`;

export const getOrganizationAddPath = () => `${prefix}/new-organization`;
export const getOrganizationSettingsPath = (organizationId) =>
  `${prefix}/${organizationId}/settings`;
export const getOrganizationGeneralSettingsPath = pipe(
  getOrganizationSettingsPath,
  append('/general'),
);
export const getOrganizationGTMSettingsPath = pipe(getOrganizationSettingsPath, append('/gtm'));
export const getOrganizationSettingsTagsPath = pipe(getOrganizationSettingsPath, append('/tags'));
export const getOrganizationTagsItemPath = (organizationId, tagId) =>
  `${getOrganizationSettingsTagsPath(organizationId)}/${tagId}`;
export const getOrganizationTagsNewItemPath = pipe(
  getOrganizationSettingsPath,
  append('/tags/add'),
);
export const getSystemReverseLookupPath = () => `${prefix}/system/reverse-lookup`;
export const getSystemTagsPath = () => `${prefix}/system/tags`;
export const getSystemTagsItemPath = (tagId) => `${prefix}/system/tags/${tagId}`;
export const getSystemTagsNewItemPath = () => `${prefix}/system/tags/add`;

export const getOrganizationEnabledFeaturesPath = pipe(
  getOrganizationSettingsPath,
  append('/enabled-features'),
);
export const getOrganizationBlacklistPath = pipe(getOrganizationSettingsPath, append('/blacklist'));
export const getOrganizationBlacklistAddPath = pipe(getOrganizationBlacklistPath, append('/add'));
export const getOrganizationFacebookPath = pipe(getOrganizationSettingsPath, append('/facebook'));
export const getOrganizationGiftbitPath = pipe(getOrganizationSettingsPath, append('/giftbit'));
export const getOrganizationLyticsPath = pipe(getOrganizationSettingsPath, append('/lytics'));
export const getOrganizationBigqueryPath = pipe(getOrganizationSettingsPath, append('/bigquery'));
export const getOrganizationMailchimpPath = pipe(getOrganizationSettingsPath, append('/mailchimp'));
export const getOrganizationTwilioPath = pipe(getOrganizationSettingsPath, append('/twilio'));
export const getOrganizationThemePath = pipe(getOrganizationSettingsPath, append('/theme'));
export const getOrganizationBackgroundJobsPath = pipe(
  getOrganizationSettingsPath,
  append('/background-jobs'),
);
export const getOrganizationBackgroundJobDetailsPath = (jobId) =>
  pipe(getOrganizationSettingsPath, append(`/background-jobs/${jobId}`));

export const getSignUpPath = () =>
  process.env.PARTICIPANT_SIGNUP_SURVEY_URL ||
  'https://www.beag.ly/app/take/1635352485S70118309076440?test-mode=true';

export const getSurveyPath = (organizationId, surveyId) =>
  `${getSurveysPath(organizationId)}/edit/${surveyId}`;
export const getSurveyCreatePath = (organizationId) => `${getSurveysPath(organizationId)}/new`;
export const getSurveyEditPath = (organizationId, surveyId) =>
  `${getSurveysPath(organizationId)}/${surveyId}/edit`;

export const getSystemSettingsPath = () => `${prefix}/system/settings`;

export const setLocation = (nextLocation) => {
  window.location = nextLocation;
};
export const setPath = (pathname, query = {}) =>
  browserHistory.push({
    pathname,
    query: omitEmpty(query),
  });
export const replaceHistory = (newPath) => browserHistory.replace(newPath);
